import { useState, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import BlockIcon from '@mui/icons-material/Block';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import orderBy from 'lodash/fp/orderBy';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import TablePagination from '@mui/material/TablePagination';
import { Modal, InputField, TextArea, Button } from '@bloomays-lib/ui.shared';
import { validateEmail } from '@bloomays-lib/utils.shared';
import {
  GqlTalentBoardInvitation,
  TalentBoardEventType,
  TalentBoardPersistedEventType,
} from '@bloomays-lib/types.shared';
import { useTheme } from '@mui/material/styles';
import { format } from 'date-fns/fp/format';
import useInvitations from '../../hooks/useInvitations';
import useTalentBoardEvents from '../../hooks/useTalentBoardEvents';
import { Logger } from '../../services/service-logger';

const logger = Logger('InvitationsDashboard');

type InvitationsDashboardProps = {
  jobId: string;
  open: boolean;
  onClose: () => void;
  interlocuteurEmails: string[];
};

const InvitationsDashboard = ({
  jobId,
  open,
  onClose,
  interlocuteurEmails,
}: InvitationsDashboardProps): React.ReactNode => {
  const theme = useTheme();
  const { t } = useTranslation(['talentBoard']);
  const {
    invitations,
    loading,
    createInvitation,
    isCreating,
    deleteInvitation,
    isDeleting,
    resendInvitation,
    isResending,
  } = useInvitations(jobId);
  const { createTalentBoardEvent } = useTalentBoardEvents();

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortAscending, setSortAscending] = useState<boolean>(false);

  const sortedInvitations = useMemo<GqlTalentBoardInvitation[]>(() => {
    return orderBy(
      [(invitation: GqlTalentBoardInvitation) => new Date(invitation.createdAt).getTime()],
      [sortAscending ? 'asc' : 'desc'],
    )(invitations);
  }, [invitations, sortAscending]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!jobId || !validateEmail(email)) {
      return;
    }

    try {
      const invitation = await createInvitation({ jobId, email, message });

      if (!invitation) {
        return;
      }

      logger.info('Traffic', {
        page: 'InvitationsDashboard',
        evt: { name: TalentBoardEventType.TalentboardInvitationCreate },
        data: { jobId, email },
      });

      void createTalentBoardEvent({
        jobId,
        date: new Date(),
        type: TalentBoardPersistedEventType.TalentboardInvitationCreate,
        data: { email },
      });
      // Réinitialiser le formulaire en cas de succès
      setEmail('');
      setMessage('');
      // Notification de succès
      toast.success(t('invitations.invitationSentSuccess') as string, {
        autoClose: 3000,
        hideProgressBar: false,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error && err.message ? err.message : (t('invitations.invitationCreateError') as string);
      toast.error(errorMessage, {
        autoClose: 5000,
        hideProgressBar: false,
      });
    }
  };

  const handleDeleteInvitation = async (id: string) => {
    const invitation = invitations.find((invitation) => invitation.id === id);

    if (!invitation) {
      return;
    }

    const deletedInvitation = await deleteInvitation({ id });

    if (deletedInvitation) {
      logger.info('Traffic', {
        page: 'InvitationsDashboard',
        evt: { name: TalentBoardEventType.TalentboardInvitationDelete },
        data: { jobId, invitationId: id },
      });

      void createTalentBoardEvent({
        jobId,
        date: new Date(),
        type: TalentBoardPersistedEventType.TalentboardInvitationDelete,
        data: { email: invitation.email },
      });
    } else {
      const errorMessage = t('invitations.invitationDeleteError') as string;
      toast.error(errorMessage, {
        autoClose: 5000,
        hideProgressBar: false,
      });
    }
  };

  const handleResendInvitation = async (id: string) => {
    const resentInvitation = await resendInvitation({ id });

    if (resentInvitation) {
      logger.info('Traffic', {
        page: 'InvitationsDashboard',
        evt: { name: TalentBoardEventType.TalentboardInvitationResend },
        data: { jobId, invitationId: id },
      });
      // Notification de succès
      const successMessage = t('invitations.invitationResentSuccess') as string;
      toast.success(successMessage, {
        autoClose: 3000,
        hideProgressBar: false,
      });
    } else {
      const errorMessage = t('invitations.invitationResendError') as string;
      toast.error(errorMessage, {
        autoClose: 5000,
        hideProgressBar: false,
      });
    }
  };

  const paginatedInvitations = sortedInvitations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSortDate = () => {
    setSortAscending(!sortAscending);
    setPage(0); // Réinitialiser la page lors du tri
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      additionnalCSS={{
        padding: '0px',
        width: 'calc(100vw - 285px)',
        height: '100vh',
        right: '0',
        top: '0',
        left: 'auto',
        transform: 'none',
        [theme.breakpoints.down('lg')]: {
          width: '90vw',
        },
        [theme.breakpoints.down('md')]: {
          backgroundColor: theme.palette.neutral[10],
          overflowY: 'auto',
          width: '100vw',
        },
      }}
      additionnalCSSCross={{
        float: 'left',
        position: 'absolute',
        top: '24px',
        left: '24px',
        zIndex: '1000',
        [theme.breakpoints.down('md')]: {
          top: '30px',
          left: '12px',
        },
      }}
    >
      <Container>
        <SideModal>
          <ShareInfoContainer>
            <ShareIcon />
            <ShareInfoText>{t('invitations.shareInfo')}</ShareInfoText>
          </ShareInfoContainer>
          <ShareForm onSubmit={handleSubmit}>
            <InputTitle>{t('invitations.personToShare')}</InputTitle>
            <InputField
              type="email"
              variant="outlined"
              placeholder={t('invitations.emailPlaceholder')}
              value={email}
              handleChange={handleEmailChange}
            />
            <InputTitle>{t('invitations.messageLabel')}</InputTitle>
            <TextArea
              name="message"
              required={false}
              placeholder={t('invitations.messagePlaceholder')}
              value={message}
              handleChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
            />
            <Button
              textButton={isCreating ? t('invitations.sharingInProgress') : t('invitations.share')}
              buttonType="submit"
              disable={isCreating || !validateEmail(email) || !email}
            />
          </ShareForm>
        </SideModal>
        <Content>
          <TableTitle variant="heading6">{t('invitations.peopleWithAccess')}</TableTitle>
          <TableContainerStyled>
            <TableStyled>
              <TableHead>
                <TableRow>
                  <TableCellStyled>
                    <SortableHeader onClick={handleSortDate}>
                      {t('invitations.invitationDate')}
                      {sortAscending === true && <ArrowUpwardIcon fontSize="small" />}
                      {sortAscending === false && <ArrowDownwardIcon fontSize="small" />}
                    </SortableHeader>
                  </TableCellStyled>
                  <TableCellStyled>{t('invitations.email')}</TableCellStyled>
                  <TableCellStyled align="right">{t('invitations.actions')}</TableCellStyled>
                </TableRow>
              </TableHead>
              <TableBody>
                {interlocuteurEmails.map((email) => (
                  <TableRow key={`interlocuteur-${email}`}>
                    <TableCellStyled>
                      <Chip label={t('invitations.defaultInterlocutor')} size="small" color="default" />
                    </TableCellStyled>
                    <TableCellStyled>{email}</TableCellStyled>
                    <TableCellStyled align="right" />
                  </TableRow>
                ))}
                {loading ? (
                  <TableRow>
                    <TableCellStyled colSpan={3} align="center">
                      {t('invitations.loading')}
                    </TableCellStyled>
                  </TableRow>
                ) : paginatedInvitations.length > 0 ? (
                  paginatedInvitations.map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCellStyled>{format('dd/MM/yyyy')(new Date(invitation.createdAt))}</TableCellStyled>
                      <TableCellStyled>{invitation.email}</TableCellStyled>
                      <TableCellStyled align="right">
                        <ActionsContainer>
                          {invitation.resentAt && (
                            <ResentDateText variant="body2">
                              {t('invitations.resentOn')} {format('dd/MM/yyyy')(new Date(invitation.resentAt))}
                            </ResentDateText>
                          )}
                          {!invitation.resentAt && (
                            <IconButton
                              onClick={() => handleResendInvitation(invitation.id)}
                              size="small"
                              aria-label={t('invitations.resendInvitation')}
                              disabled={isResending || isDeleting}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            onClick={() => handleDeleteInvitation(invitation.id)}
                            size="small"
                            aria-label={t('invitations.deleteInvitation')}
                            color="error"
                            disabled={isDeleting || isResending}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </ActionsContainer>
                      </TableCellStyled>
                    </TableRow>
                  ))
                ) : interlocuteurEmails.length === 0 ? (
                  <TableRow>
                    <TableCellStyled colSpan={3} align="center">
                      {t('invitations.noSharedAccess')}
                    </TableCellStyled>
                  </TableRow>
                ) : null}
              </TableBody>
            </TableStyled>
          </TableContainerStyled>
          {sortedInvitations.length > 0 && (
            <TablePagination
              component="div"
              count={sortedInvitations.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage={t('rowsPerPage')}
            />
          )}
        </Content>
      </Container>
    </Modal>
  );
};

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  height: '100%',
  backgroundColor: theme.palette.neutral.white,
  [theme.breakpoints.down('md')]: {
    backgroundColor: theme.palette.neutral[10],
    position: 'unset',
    flexDirection: 'column',
  },
}));

const SideModal = styled('div')(({ theme }) => ({
  padding: '60px 24px 0',
  minWidth: '364px',
  maxWidth: '364px',
  backgroundColor: theme.palette.neutral[10],
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    marginTop: '0px',
    minWidth: 'unset',
    maxWidth: 'unset',
    overflowY: 'unset',
  },
}));

const Content = styled('div')(({ theme }) => ({
  width: '100%',
  padding: '24px',
  backgroundColor: theme.palette.red[20],
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const ShareInfoContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '12px',
  backgroundColor: theme.palette.neutral.white,
  borderRadius: '10px',
  padding: '12px',
}));

const ShareIcon = styled(ShareOutlinedIcon)(({ theme }) => ({
  width: '24px',
  height: '24px',
  color: theme.palette.neutral.black,
}));

const ShareInfoText = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: '400',
  lineHeight: '19.6px',
  color: theme.palette.neutral.black,
}));

const ShareForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  borderRadius: '10px',
  padding: '12px',
}));

const InputTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '19.6px',
  color: theme.palette.neutral.black,
}));

const TableTitle = styled(Typography)(({ theme }) => ({
  marginBottom: '24px',
}));

const TableStyled = styled(Table)({
  tableLayout: 'fixed',
  width: '100%',
});

const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
  backgroundColor: theme.palette.neutral.white,
  borderRadius: '10px',
  marginBottom: '16px',
  overflowX: 'auto',
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.neutral[10],
      },
      '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.neutral.white,
      },
    },
  },
}));

const TableCellStyled = styled(TableCell)(({ theme }) => ({
  padding: '16px',
  fontSize: '14px',
  color: theme.palette.neutral.black,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:nth-of-type(1)': { width: '25%' },
  '&:nth-of-type(2)': { width: '35%' },
  '&:nth-of-type(3)': { width: '40%' },
}));

const ActionsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '8px',
});

const ResentDateText = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.neutral[60],
  marginRight: '8px',
}));

const SortableHeader = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  userSelect: 'none',
  maxWidth: '100%',
  minWidth: 0,
  '&:hover': {
    opacity: 0.7,
  },
});

export default InvitationsDashboard;
